import { Booking } from '@/types'
import { FileUp, Search, Calendar, User, CreditCard } from 'lucide-react'

interface TransactionsTableProps {
    bookings: Booking[]
    loading: boolean
}

export default function TransactionsTable({ bookings, loading }: TransactionsTableProps) {
    if (loading) {
        return <div className="h-64 bg-gray-50 rounded-[2rem] animate-pulse" />
    }

    if (bookings.length === 0) {
        return (
            <div className="bg-white p-10 rounded-[2.5rem] border border-gray-100 text-center">
                <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Nenhuma transação encontrada</p>
            </div>
        )
    }

    return (
        <div className="bg-white rounded-[2.5rem] border border-[#001c28]/5 shadow-xl shadow-[#001c28]/5 overflow-hidden">
            <div className="p-8 border-b border-[#001c28]/5 flex justify-between items-center">
                <h3 className="text-xl font-black text-[#001c28] uppercase italic tracking-tighter">Transações Recentes</h3>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-[#001c28]/[0.02] border-b border-[#001c28]/5">
                        <tr>
                            <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Data</th>
                            <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Reserva ID</th>
                            <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Cliente</th>
                            <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Status</th>
                            <th className="px-6 py-4 text-right text-[10px] font-black uppercase tracking-[0.2em] text-[#001c28]/40">Valor</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#001c28]/5">
                        {bookings.map((booking) => {
                            const date = booking.createdAt?.toDate ? booking.createdAt.toDate() : new Date()

                            return (
                                <tr key={booking.id} className="group hover:bg-[#001c28]/[0.02] transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-[#001c28] text-sm">
                                                {date.toLocaleDateString('pt-BR')}
                                            </span>
                                            <span className="text-xs text-[#001c28]/50 font-medium">
                                                {date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="font-mono text-xs text-[#001c28]/60 bg-gray-100 px-2 py-1 rounded-lg">
                                            {booking.id.slice(0, 8)}...
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-[#001c28]/10 flex items-center justify-center text-[#001c28]">
                                                <User className="w-3 h-3" />
                                            </div>
                                            <span className="text-sm font-medium text-[#001c28]/80">
                                                {booking.userName || 'Sem nome'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`
                                            inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border
                                            ${booking.status === 'CONFIRMED' || booking.status === 'COMPLETED'
                                                ? 'bg-green-50 text-green-700 border-green-200'
                                                : booking.status === 'CANCELED'
                                                    ? 'bg-red-50 text-red-700 border-red-200'
                                                    : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                            }
                                        `}>
                                            {booking.status === 'CONFIRMED' ? 'Pago' : booking.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <span className={`font-bold ${booking.status === 'CANCELED' ? 'text-gray-400 line-through' : 'text-[#001c28]'}`}>
                                            R$ {booking.totalPrice?.toFixed(2)}
                                        </span>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
